import * as React from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { ResponsiveContainer } from "@/components/layout/responsive-grid"
import { Bug, User, Loader2, Key, Copy, Eye, EyeOff, RotateCw } from "lucide-react"
import { forceResetDatabases } from "@/utils/database-cleanup"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { useAuthStore } from "@/stores/auth.store"
import { UserService } from "@/effects/user/user.service"
import { MainLayer } from "@/effects/layers"
import { runEffect } from "@/effects/runtime"
import { toast } from "sonner"
import * as Effect from 'effect/Effect'
import { pipe } from 'effect/Function'

export const SettingsPage: React.FC = () => {
  const { user } = useAuthStore()
  const [name, setName] = React.useState(user?.name || "")
  const [isLoading, setIsLoading] = React.useState(false)
  const [apiKey, setApiKey] = React.useState<string | null>(null)
  const [apiKeyLoading, setApiKeyLoading] = React.useState(false)
  const [apiKeyVisible, setApiKeyVisible] = React.useState(false)
  const [apiKeyRotating, setApiKeyRotating] = React.useState(false)

  const maskApiKey = (value: string) => {
    if (value.length <= 8) return value
    return `${value.slice(0, 4)}...${value.slice(-4)}`
  }

  const fetchApiKey = async () => {
    setApiKeyLoading(true)
    try {
      const program = pipe(
        UserService,
        Effect.flatMap((service) => service.getApiKey()),
        Effect.provide(MainLayer)
      )
      const result = await runEffect(program)
      setApiKey(result.apiKey)
    } catch (error) {
      console.error("Failed to fetch API key:", error)
      toast.error("Failed to fetch API key")
    } finally {
      setApiKeyLoading(false)
    }
  }

  const handleRotateApiKey = async () => {
    setApiKeyRotating(true)
    try {
      const program = pipe(
        UserService,
        Effect.flatMap((service) => service.rotateApiKey()),
        Effect.provide(MainLayer)
      )
      const result = await runEffect(program)
      setApiKey(result.apiKey)
      setApiKeyVisible(true)
      toast.success("API key rotated")
    } catch (error) {
      console.error("Failed to rotate API key:", error)
      toast.error("Failed to rotate API key")
    } finally {
      setApiKeyRotating(false)
    }
  }

  const handleCopyApiKey = async () => {
    if (!apiKey) return
    try {
      await navigator.clipboard.writeText(apiKey)
      toast.success("API key copied")
    } catch (error) {
      console.error("Failed to copy API key:", error)
      toast.error("Failed to copy API key")
    }
  }

  React.useEffect(() => {
    if (user?.id) {
      fetchApiKey()
    }
  }, [user?.id])

  const handleResetDatabase = () => {
    forceResetDatabases()
    toast.success("Database reset successfully")
  }

  const handleProfileSave = async () => {
    if (!user?.id) {
      toast.error("User not found")
      return
    }

    if (!name.trim()) {
      toast.error("Name cannot be empty")
      return
    }

    setIsLoading(true)

    try {
      const program = pipe(
        UserService,
        Effect.flatMap((service) => service.updateProfile(user.id, { name: name.trim() })),
        Effect.provide(MainLayer)
      )

      const updatedUser = await runEffect(program)

      // Update the user in the auth store
      const currentState = useAuthStore.getState()
      useAuthStore.setState({
        ...currentState,
        user: updatedUser
      })

      toast.success("Profile updated successfully")
    } catch (error) {
      console.error("Failed to update profile:", error)
      toast.error("Failed to update profile")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <ResponsiveContainer size="lg">
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold">Settings</h1>
          {/* <p className="text-muted-foreground">
            Manage your account preferences and settings
          </p> */}
        </div>

        <div className="grid gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bug className="h-5 w-5" />
                Debug
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <Button onClick={handleResetDatabase}>
                Reset database
              </Button>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Profile Information
              </CardTitle>
              <CardDescription>
                Update your personal information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={isLoading}
                    placeholder="Enter your name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    value={user?.email || ""}
                    disabled
                    readOnly
                    className="bg-muted"
                  />
                </div>
              </div>

              <Button
                onClick={handleProfileSave}
                disabled={isLoading || !name.trim() || name === user?.name}
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isLoading ? "Saving..." : "Save Changes"}
              </Button>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                API Key
              </CardTitle>
              <CardDescription>
                Use this key as `MOITIE_API_KEY` for the CLI. Regenerating invalidates the old key.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="api-key">API Key</Label>
                <div className="flex flex-col gap-2 md:flex-row md:items-center">
                  <Input
                    id="api-key"
                    value={apiKey ? (apiKeyVisible ? apiKey : maskApiKey(apiKey)) : ""}
                    disabled={apiKeyLoading}
                    readOnly
                    className="font-mono"
                    placeholder={apiKeyLoading ? "Loading..." : "No API key found"}
                  />
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setApiKeyVisible((prev) => !prev)}
                      disabled={!apiKey}
                    >
                      {apiKeyVisible ? <EyeOff className="mr-2 h-4 w-4" /> : <Eye className="mr-2 h-4 w-4" />}
                      {apiKeyVisible ? "Hide" : "Reveal"}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleCopyApiKey}
                      disabled={!apiKey}
                    >
                      <Copy className="mr-2 h-4 w-4" />
                      Copy
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={handleRotateApiKey}
                      disabled={apiKeyRotating}
                    >
                      {apiKeyRotating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCw className="mr-2 h-4 w-4" />}
                      {apiKeyRotating ? "Rotating..." : "Regenerate"}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </ResponsiveContainer>
  )
}
